import React, { useState } from "react";
import { Button } from "primereact/button";

import { InputText } from "primereact/inputtext";
import {
  ageGroup,
  ComplaintDetailsDTO,
  ComplaintMatrimonialSituation,
  disabledInfoList,
  DropDownList,
  SituationTranslations,
  StepCallBack,
} from "@modules/plaintes/plaintes.model.ts";
import { RadioButton, RadioButtonChangeEvent } from "primereact/radiobutton";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  guichetUnique,
  setStepReasonValue,
  stepReasonValue,
} from "@core/slice/ComplaintSlice.ts";
import { capitalizeFirstLetter, formatFullName } from "@utils/appUtils.ts";
import { getPhoneMaxLength } from "@modules/setting/userManagement/user.model.ts";

const ConcernedPerson: React.FC<StepCallBack> = ({
  onNext,
  onPrevious,
  isResolvedComplaint,
}) => {
  const dispatch = useAppDispatch();
  const stepReason = useAppSelector(stepReasonValue);
  const [victimInfos, setVictimInfos] = useState<ComplaintDetailsDTO>({
    lastName: "",
    gender: "",
    ageGroup: "",
    disability: false,
    cin: "",
    householdId: "",
    individualId: "",
    parentCohabitation: false,
    disabilityInfo: "",
    contactNumber: "",
    contactAddress: "",
    fatherName: "",
    motherName: "",
    personType: null,
    martialStatus: null,
    firstName: "",
    student: undefined,
    employee: undefined,
  });

  const [selectedAge, setSelectedAge] = useState<DropDownList>();
  const [isMajor, setIsMajor] = useState<boolean>(false);
   const guichetUniqueData = useAppSelector(guichetUnique);

  const situation = Object.entries(ComplaintMatrimonialSituation).map(
    ([key, value]) => ({
      name: SituationTranslations[value as ComplaintMatrimonialSituation],
      code: key,
    })
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [cin, setCin] = useState(Array(12).fill("")); // 12 cases pour le CIN

  const [errors, setErrors] = useState({
    name: "",
    address: "",
    disabilityInfo: "",
    sex: "",
    ageGroup: "",
    contactNumber: "",
  });

  React.useEffect(() => {
    console.log("stepReason", stepReason);
    if (stepReason && stepReason.victimStep) {
      const victim = stepReason.victimStep;
      const findAge = ageGroup.find((e) => e.code === victim.ageGroup);
      if (findAge) {
        setSelectedAge(findAge);
      }
      // Le Guichet Unique renvoie null sur les champs non renseignes alors que
      // ComplaintDetailsDTO les type en string : on normalise avant de poser le state,
      // sinon le rendu casse (getPhoneMaxLength, trim, toUpperCase...).
      setVictimInfos((prev) => ({
        ...prev,
        ...victim,
        lastName: victim.lastName ?? "",
        firstName: victim.firstName ?? "",
        cin: victim.cin ?? "",
        householdId: victim.householdId ?? "",
        individualId: victim.individualId ?? "",
        contactNumber: victim.contactNumber ?? "",
        contactAddress: victim.contactAddress ?? "",
        fatherName: victim.fatherName ?? "",
        motherName: victim.motherName ?? "",
        disability: victim.disability ?? false,
        parentCohabitation: victim.parentCohabitation ?? false,
      }));
      setFirstName(victim.firstName ?? "");
      setLastName(victim.lastName ?? "");
      if (!victim.cin || victim.cin.length === 0) {
        setCin(Array(12).fill(""));
      } else {
        setCin(victim.cin.split("").map(Number));
      }
    }
  }, [stepReason]);

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let updatedErrors = { ...errors };
    setVictimInfos((prevInfos) => ({
      ...prevInfos,
      [name]: value,
    }));
    if (name === "lastName") {
      const formattedValue = value.toUpperCase();
      setLastName(formattedValue);
      setVictimInfos((prevInfos) => ({
        ...prevInfos,
        lastName: formattedValue,
      }));
      updatedErrors.name = "";
    }
    if (name === "firstName") {
      const formattedValue = capitalizeFirstLetter(value);
      setFirstName(formattedValue);
      setVictimInfos((prevInfos) => ({
        ...prevInfos,
        firstName: formattedValue,
      }));
      updatedErrors.name = "";
    }

    if (name === "contactAddress") {
      updatedErrors.address = "";
    }
    if (name === "ageGroup") {
      updatedErrors.ageGroup = "";
    }
    if (name === "gender") {
      updatedErrors.sex = "";
    }
    if (name === "contactNumber") {
      updatedErrors.contactNumber = "";
    }

    setErrors(updatedErrors);
  };

  React.useEffect(() => {
    if (stepReason?.victimStep && stepReason.victimStep.ageGroup) {
      const age = +stepReason.victimStep.ageGroup.split("_")[3];
      setIsMajor(age < 18 ? false : true);
    }
  }, [stepReason]);

  const phoneRegex = /^(032|033|034|038|037|020)\d{7}$/;
  const internationalPhoneRegex = /^\+261(32|33|34|38|37|20)\d{7}$/;

  // console.log("step", stepReason);
  // console.log("victimInfos", victimInfos, 'hhhh', guichetUniqueData);

  const handleBlurPhone = (e: React.FocusEvent<HTMLInputElement>) => {
    let updatedErrors = { ...errors };
    const value = e.target.value.replace(/\s/g, "");

    if (
      !phoneRegex.test(value) &&
      !internationalPhoneRegex.test(value) &&
      value !== ""
    ) {
      updatedErrors.contactNumber =
        "Numéro téléphone valide: 032|033|034|038|037|020xxxxxxx  +261xxxxxxxxx (ex: 032xxxxxxx ou +26132xxxxxxx)";
    } else {
      updatedErrors.contactNumber = "";
    }

    setErrors(updatedErrors);
  };

  function handleCINChange(e: React.ChangeEvent<HTMLInputElement>, index: any) {
    const value = e.target.value;

    if (!/^\d?$/.test(value)) return;

    const newCin = [...cin];
    newCin[index] = value.slice(0, 1);
    setCin(newCin);

    const allFilled = newCin.every((char) => char.length === 1);
    if (allFilled) {
      const completeCIN = newCin.join("");
      setVictimInfos((prev) => ({
        ...prev,
        cin: completeCIN,
      }));
    }

    // Passage automatique au champ suivant si un chiffre est saisi
    if (value && index < cin.length - 1) {
      document.getElementById(`cin-input-${index + 1}`)?.focus();
    }
  }

  function handlePreviousStep() {
    if (onPrevious) {
      if (stepReason) {
        dispatch(
          setStepReasonValue({
            ...stepReason,
            victimStep: {
              ageGroup: victimInfos.ageGroup,
              cin: victimInfos.cin,
              contactAddress: victimInfos.contactAddress,
              contactNumber: victimInfos.contactNumber,
              disability: victimInfos.disability,
              disabilityInfo: victimInfos.disabilityInfo,
              fatherName: victimInfos.fatherName,
              gender: victimInfos.gender,
              householdId: victimInfos.householdId,
              individualId: victimInfos.individualId,
              firstName: victimInfos.firstName,
              martialStatus: victimInfos.martialStatus,
              motherName: victimInfos.motherName,
              parentCohabitation: victimInfos.parentCohabitation,
              personType: victimInfos.personType,
              lastName: victimInfos.lastName,
              employee: victimInfos.employee,
              student: victimInfos.student,
            },
          })
        );
        onPrevious();
      }
    }
  }
  function handleNextStep() {
    let formErrors = { ...errors };
    if (victimInfos.lastName.trim() === "") {
      formErrors.name = "Veuillez remplir le champ Nom";
    } else {
      formErrors.name = "";
    }

    if (victimInfos.contactAddress.trim() === "") {
      formErrors.address = "Veuillez remplir le champ adresse";
    } else {
      formErrors.address = "";
    }

    if (victimInfos.disability === true && victimInfos.disabilityInfo === "") {
      formErrors.disabilityInfo =
        "Veuillez specifier le type de votre handicap";
    } else formErrors.disabilityInfo = "";

    if (victimInfos.gender === "") {
      formErrors.sex = "Veuillez specifier le sexe";
    }

    if (victimInfos.ageGroup === "") {
      formErrors.ageGroup = "Veuillez specifier l'âge";
    }
    if (
      !phoneRegex.test(victimInfos.contactNumber) &&
      !internationalPhoneRegex.test(victimInfos.contactNumber) &&
      victimInfos.contactNumber !== ""
    ) {
      formErrors.contactNumber =
        "Numéro téléphone valide: 032|033|034|038|037|020xxxxxxx  +261xxxxxxxxx (ex: 032xxxxxxx ou +26132xxxxxxx)";
    } else formErrors.contactNumber = "";

    setErrors(formErrors);
    if (
      !formErrors.name &&
      !formErrors.address &&
      !formErrors.sex &&
      !formErrors.ageGroup &&
      !formErrors.contactNumber &&
      !formErrors.disabilityInfo
    ) {
      if (stepReason) {
        dispatch(
          setStepReasonValue({
            ...stepReason,
            victimStep: {
              ageGroup: victimInfos.ageGroup,
              cin: victimInfos.cin,
              contactAddress: victimInfos.contactAddress,
              contactNumber: victimInfos.contactNumber,
              disability: victimInfos.disability,
              disabilityInfo: victimInfos.disabilityInfo,
              fatherName: victimInfos.fatherName,
              gender: victimInfos.gender,
              householdId: victimInfos.householdId,
              individualId: victimInfos.individualId,
              firstName: victimInfos.firstName,
              martialStatus: victimInfos.martialStatus,
              motherName: victimInfos.motherName,
              parentCohabitation: victimInfos.parentCohabitation,
              personType: victimInfos.personType,
              lastName: victimInfos.lastName,
              employee: victimInfos.employee,
              student: victimInfos.student,
            },
          })
        );
        onNext();
      }
    }
  }

  return (
    <div className="column gap-[20px] card-stepper">
      <div className="stitle">Informations sur la personne concernée</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ">
        <div className="flex flex-wrap gap-3">
          <div className="column gap-2 w-full">
            <label htmlFor="lastName">Nom (obligatoire)</label>
            <InputText
              maxLength={255}
              id="lastName"
              name="lastName"
              placeholder="Saisir le nom"
              onChange={handleChangeInput}
              value={lastName.toUpperCase()}
            />
            {errors.name && (
              <small className="text-red-600">{errors.name}</small>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="flex-auto column gap-2 w-full">
            <label htmlFor="firstName">Prénom(s)</label>
            <InputText
              id="firstName"
              name="firstName"
              placeholder="Saisir le prénom"
              onChange={handleChangeInput}
              value={firstName}
              maxLength={255}
            />
          </div>
        </div>
        <div className="column gap-1">
          Sexe (obligatoire)
          <div className="flex flex-wrap gap-3">
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="homme"
                name="sex"
                value={victimInfos.gender}
                onChange={(e: RadioButtonChangeEvent) => {
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    gender: "MALE",
                    age: "",
                  }));
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    sex: "",
                    age: "",
                  }));
                }}
                checked={victimInfos.gender === "MALE"}
              />

              <label htmlFor="Homme" className="ml-2">
                Homme
              </label>
            </div>
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="Femme"
                name="sex"
                value={victimInfos.gender}
                onChange={(e: RadioButtonChangeEvent) => {
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    gender: "FEMALE",
                    age: "",
                  }));
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    sex: "",
                    age: "",
                  }));
                }}
                checked={victimInfos.gender === "FEMALE"}
              />

              <label htmlFor="Femme" className="ml-2">
                Femme
              </label>
            </div>
          </div>
          {errors.sex && <small className="text-red-600">{errors.sex}</small>}
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="flex-auto column gap-2 w-full">
            <label htmlFor="age">Âge (obligatoire)</label>

            <Dropdown
              id="age"
              value={selectedAge}
              onChange={(e: DropdownChangeEvent) => {
                setSelectedAge(e.value);
                setVictimInfos((prevInfos) => ({
                  ...prevInfos,
                  ageGroup: e.value.code,
                }));
                const age = +e.value.code.split("_")[3];
                setIsMajor(age < 18 ? false : true);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  ageGroup: "", // Effacer l'erreur de l'âge
                }));
              }}
              options={ageGroup}
              optionLabel="name"
              placeholder="---Tranche d'âge---"
              className="w-full md:w-14rem"
            />
          </div>
          {errors.ageGroup && (
            <small className="text-red-600">{errors.ageGroup}</small>
          )}
        </div>
        <div className="column gap-1">
          <div className="flex flex-row flex-wrap gap-3">
            <div className="flex-1 ">
              <div className="flex flex-wrap gap-3">
                <div className="column gap-2 w-full">
                  <label htmlFor="individualId">ID individu </label>
                  <InputText
                    id="individualId"
                    name="individualId"
                    value={victimInfos.individualId}
                    placeholder="ID individu"
                    aria-describedby="username-help"
                    onChange={handleChangeInput}
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 ">
              <div className="flex flex-wrap gap-3">
                <div className="column gap-2 w-full">
                  <label htmlFor="householdId">ID ménage</label>
                  <InputText
                    id="householdId"
                    name="householdId"
                    value={victimInfos.householdId}
                    placeholder="ID ménage"
                    aria-describedby="username-help"
                    onChange={handleChangeInput}
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {isMajor ? (
          <div className="column gap-1">
            <div className="flex flex-wrap gap-3">
              <div className="column gap-2 w-full">
                <label htmlFor="username">Numéro CIN</label>

                <div className="w-full flex justify-content-center gap-3 flex-wrap">
                  {cin.map((value, index) => (
                    <InputText
                      key={`${index}`}
                      id={`cin-input-${index}`}
                      value={value}
                      onChange={(e) => handleCINChange(e, index)}
                      maxLength={1} // Limite la saisie à un seul caractère
                      style={{
                        width: "calc(100% /12)",
                        height: "38px",
                        textAlign: "center",
                        fontSize: "12px",
                        borderRadius: "4px",
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="column gap-1">
            <div className="flex flex-wrap gap-3"></div>
          </div>
        )}
        <div className="column gap-1">
          Handicapé(e)
          <div className="flex flex-wrap gap-3">
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="handicap"
                name="handicap"
                value={victimInfos.disability}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    disability: true,
                  }))
                }
                checked={victimInfos.disability === true}
              />
              <label htmlFor="handicap" className="ml-2">
                Oui
              </label>
            </div>
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="nohandicap"
                name="nohandicap"
                value={victimInfos.disability}
                onChange={(e: RadioButtonChangeEvent) => {
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    disability: false,
                    disabilityInfo: "",
                  }));
                  setErrors((error) => ({
                    ...error,
                    disabilityInfo: "",
                  }));
                }}
                checked={victimInfos.disability === false}
              />
              <label htmlFor="nohandicap" className="ml-2">
                Non
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="flex-auto column gap-2 w-full">
            <label htmlFor="firstName">
              Remarque spéciale{" "}
              {victimInfos.disability === true ? "(obligatoire)" : ""}
            </label>
            <Dropdown
              id="situation"
              value={victimInfos.disabilityInfo}
              onChange={(e: DropdownChangeEvent) => {
                setErrors({ ...errors, disabilityInfo: "" });
                setVictimInfos((prevInfos) => ({
                  ...prevInfos,
                  disabilityInfo: e.value,
                }));
              }}
              options={disabledInfoList}
              optionLabel="name"
              placeholder="---Type de handicap---"
              className="w-full md:w-14rem"
              disabled={!victimInfos.disability}
            />

            {errors.disabilityInfo && (
              <small className="text-red-600">{errors.disabilityInfo}</small>
            )}
          </div>
        </div>
        <div className="column gap-1">
          Etudiant(e)
          <div className="flex flex-wrap gap-3">
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="student"
                name="student"
                value={victimInfos.student}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    student: "YES",
                  }))
                }
                checked={victimInfos.student === "YES"}
              />
              <label htmlFor="student" className="ml-2">
                Oui
              </label>
            </div>
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="notStudent"
                name="notStudent"
                value={victimInfos.student}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    student: "NO",
                  }))
                }
                checked={victimInfos.student === "NO"}
              />
              <label htmlFor="notStudent" className="ml-2">
                Non
              </label>
            </div>
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="noInformation"
                name="noInformation"
                value={victimInfos.student}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    student: "UNKNOWN",
                  }))
                }
                checked={victimInfos.student === "UNKNOWN"}
              />
              <label htmlFor="notStudent" className="ml-2">
                Aucune information
              </label>
            </div>
          </div>
        </div>
        <div className="column gap-1">
          Employé(e)
          <div className="flex flex-wrap gap-3">
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="employee"
                name="employee"
                value={victimInfos.employee}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    employee: "YES",
                  }))
                }
                checked={victimInfos.employee === "YES"}
              />
              <label htmlFor="employee" className="ml-2">
                Oui
              </label>
            </div>
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="notEmployee"
                name="notEmployee"
                value={victimInfos.employee}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    employee: "NO",
                  }))
                }
                checked={victimInfos.employee === "NO"}
              />
              <label htmlFor="notEmployee" className="ml-2">
                Non
              </label>
            </div>
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="noInformationEmployee"
                name="noInformationEmployee"
                value={victimInfos.employee}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    employee: "UNKNOWN",
                  }))
                }
                checked={victimInfos.employee === "UNKNOWN"}
              />
              <label htmlFor="noInformationEmployee" className="ml-2">
                Aucune information
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="column gap-2 w-full">
            <label htmlFor="fatherName">Père</label>
            <InputText
              id="fatherName"
              name="fatherName"
              value={victimInfos.fatherName ? formatFullName(victimInfos.fatherName) : ""}
              placeholder=""
              onChange={handleChangeInput}
              maxLength={255}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="column gap-2 w-full">
            <label htmlFor="motherName">Mère</label>
            <InputText
              id="motherName"
              name="motherName"
              value={victimInfos.motherName ? formatFullName(victimInfos.motherName) : ""}
              placeholder=""
              onChange={handleChangeInput}
              maxLength={255}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="flex-auto column gap-2 w-full">
            <label htmlFor="situation">Situation Matrimoniale</label>

            <Dropdown
              id="situation"
              value={
                situation.find((s) => s.code === victimInfos.martialStatus) ||
                null
              }
              onChange={(e: DropdownChangeEvent) =>
                setVictimInfos((prevInfos) => ({
                  ...prevInfos,
                  martialStatus: e.value.code,
                }))
              }
              options={situation}
              optionLabel="name"
              placeholder="---Situation matrimoniale---"
              className="w-full md:w-14rem"
            />
          </div>
        </div>

        <div className="column gap-1">
          Vit avec ses parents
          <div className="flex flex-wrap gap-3">
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="livedWithParents"
                name="livedWithParents"
                value={victimInfos.parentCohabitation}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    parentCohabitation: true,
                  }))
                }
                checked={victimInfos.parentCohabitation === true}
              />
              <label htmlFor="livedWithParents" className="ml-2">
                Oui
              </label>
            </div>
            <div className="flex align-items-center radio">
              <RadioButton
                inputId="notlivedWithParents"
                name="notlivedWithParents"
                value={victimInfos.parentCohabitation}
                onChange={(e: RadioButtonChangeEvent) =>
                  setVictimInfos((prevInfos) => ({
                    ...prevInfos,
                    parentCohabitation: false,
                  }))
                }
                checked={victimInfos.parentCohabitation === false}
              />
              <label htmlFor="notlivedWithParents" className="ml-2">
                Non
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="column gap-2 w-full">
            <label htmlFor="contactNumber">Numero téléphone</label>
            <InputText
              id="contactNumber"
              name="contactNumber"
              value={victimInfos.contactNumber}
              placeholder=""
              onChange={handleChangeInput}
              onBlur={handleBlurPhone}
              maxLength={getPhoneMaxLength(victimInfos.contactNumber)}
            />
          </div>
          {errors.contactNumber && (
            <small className="text-red-600">{errors.contactNumber}</small>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="column gap-2 w-full">
            <label htmlFor="contactAddress">Adresse (obligatoire)</label>
            <InputText
              maxLength={255}
              id="contactAddress"
              name="contactAddress"
              value={victimInfos.contactAddress}
              placeholder=""
              onChange={handleChangeInput}
            />
            {errors.address && (
              <small className="text-red-600">{errors.address}</small>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-3 justify-content-end">
        <div className="flex justify-content-end">
          <Button
            label="Précédent"
            className="btn btn-secondary w-[120px]"
            onClick={handlePreviousStep}
          />
        </div>
        <div className="flex justify-content-end">
          <Button
            label="Suivant"
            className="btn btn-primary w-[120px]"
            onClick={handleNextStep}
          />
        </div>
      </div>
    </div>
  );
};

export default ConcernedPerson;
